import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { DownloadButtons } from "../components/DownloadButtons";
import api, { API } from "../lib/api";
import { useSeo } from "../lib/seo";

export default function Success() {
  useSeo({ title: "Order complete", description: "Your Faithful Store download is ready.", path: "/success", noindex: true });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [token, setToken] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.status === "paid") {
          setStatus("paid");
          setOrder(r.data);
          if (r.data.download_token) setToken(r.data.download_token);
        } else if (tries < 15) {
          setTries((n) => n + 1);
          setTimeout(poll, 2000);
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
      <div className="card-flat p-12 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-[#0a192f] mx-auto animate-spin" />
            <h1 className="font-serif text-3xl mt-6 text-[#0a192f]">Confirming your purchase…</h1>
            <p className="text-[#4a4a4a] mt-2">Hang tight, we're finalizing everything.</p>
          </>
        )}
        {status === "paid" && order?.kind === "physical" && (
          <div data-testid="physical-success">
            <CheckCircle2 className="w-12 h-12 text-[#d4af37] mx-auto" />
            <h1 data-testid="success-title" className="font-serif text-4xl mt-6 text-[#0a192f]">Thank you. Your order is confirmed.</h1>
            <p className="text-[#4a4a4a] mt-3">We're packing <strong>{order.product_name}</strong>{order.quantity > 1 ? ` ×${order.quantity}` : ""}. A confirmation is on its way to <strong>{order.email}</strong>, and we'll email tracking as soon as it ships.</p>
            {order.ship_address?.line1 && (
              <div className="mt-8 inline-block text-left bg-[#fdfbf7] border border-[#e5e5e5] px-6 py-4 text-sm" data-testid="ship-to">
                <div className="overline text-[#4a4a4a] mb-2">Shipping to</div>
                <div className="font-semibold text-[#0a192f]">{order.ship_name}</div>
                <div>{order.ship_address.line1}{order.ship_address.line2 ? `, ${order.ship_address.line2}` : ""}</div>
                <div>{[order.ship_address.city, order.ship_address.state, order.ship_address.postal_code].filter(Boolean).join(" ")}</div>
                <div>{order.ship_address.country}</div>
              </div>
            )}
            <div className="mt-6 text-sm text-[#4a4a4a]" data-testid="order-totals">
              Items ${(order.amount_cents / 100).toFixed(2)} · Shipping ${((order.shipping_cents || 0) / 100).toFixed(2)} · Tax ${((order.tax_cents || 0) / 100).toFixed(2)} · <strong className="text-[#0a192f]">Total ${((order.total_cents || order.amount_cents) / 100).toFixed(2)}</strong>
            </div>
            <div className="mt-6">
              <Link to="/shop" className="text-sm text-[#0a192f] hover:text-[#d4af37] font-semibold">Continue exploring →</Link>
            </div>
          </div>
        )}
        {status === "paid" && order?.kind !== "physical" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#d4af37] mx-auto" />
            <h1 data-testid="success-title" className="font-serif text-4xl mt-6 text-[#0a192f]">{order?.gift_to_email ? "Your gift is on its way." : "Thank you. Enjoy the read."}</h1>
            {order?.gift_to_email && (
              <p className="mt-3 text-[#0a192f] bg-[#fdfbf7] border border-[#d4af37]/40 px-4 py-3 inline-block" data-testid="gift-sent-note">
                We've emailed the download link and your note to <strong>{order.gift_to_email}</strong>.
              </p>
            )}
            <p className="text-[#4a4a4a] mt-3">
              Your download is ready below. A copy of this link is also on the way to your inbox — and you can always re-download from <Link to="/library" className="underline">My Library</Link>.
            </p>
            {token && order?.order_id && (
              <div className="mt-8 flex flex-col items-center gap-5" data-testid="download-links">
                {(order.items?.length ? order.items : [{ product_id: order.product_id, name: "your product" }]).map((it, idx) => (
                  <div key={it.product_id} className="text-center">
                    {order.items?.length > 1 && <div className="font-serif text-lg text-[#0a192f] mb-2">{it.name}</div>}
                    <DownloadButtons baseUrl={`${API}/download/${order.order_id}?token=${token}&item=${it.product_id}`} item={it} primaryTestId={idx === 0 ? "download-link" : `download-link-${idx}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6">
              <Link to="/shop" className="text-sm text-[#0a192f] hover:text-[#d4af37] font-semibold">Continue exploring →</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-serif text-3xl text-[#0a192f]">Payment is still processing.</h1>
            <p className="text-[#4a4a4a] mt-3">Refresh this page in a minute or check your email.</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-serif text-3xl text-[#0a192f]">Something went wrong.</h1>
            <Link to="/shop" className="btn-navy inline-flex mt-6">Back to shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
